import { useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PackageOpen, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Spinner } from '@/components/ui/spinner';
import { WizardStepper } from '@/components/deploy/WizardStepper';
import { DeploySummary } from '@/components/deploy/DeploySummary';
import { SourceStep } from '@/components/deploy/SourceStep';
import { TargetStep } from '@/components/deploy/TargetStep';
import { ConfigureStep, type ConfigureField } from '@/components/deploy/ConfigureStep';
import type { EnvDraft } from '@/components/deploy/EnvVarEditor';
import { ReviewStep, type ReviewTarget } from '@/components/deploy/ReviewStep';
import { ComposePreview } from '@/components/deploy/ComposePreview';
import { api } from '@/lib/api';
import { errorDetailCode } from '@/lib/apiErrors';
import { useDeployData } from '@/lib/hooks/useDeployData';
import {
  domainChoiceLabel,
  effectiveDomainChoice,
  effectiveTargetMode,
  lockedEndpointIds,
  mapDeployError,
  preselectedEndpointId,
  rankEndpoints,
  traefikBlocked,
} from '@/lib/deployPlan';
import { computeStepValidity } from '@/lib/deployGating';
import { customHostLabel, subdomainFallbackNotice } from '@/lib/subdomain';
import {
  initialWizardState,
  wizardReducer,
  type WizardStep,
} from '@/lib/deployWizard';

const STEP_LIST: { id: WizardStep; label: string }[] = [
  { id: 'source', label: 'Source' },
  { id: 'target', label: 'Target' },
  { id: 'configure', label: 'Configure' },
  { id: 'review', label: 'Review' },
];

function LoadingSkeleton() {
  return (
    <div>
      <div aria-hidden className="space-y-4">
        <div className="h-10 animate-pulse rounded-md bg-muted" />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="h-72 animate-pulse rounded-lg bg-muted" />
          <div className="h-48 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
      <p role="status" className="sr-only">
        Loading catalogs and endpoints
      </p>
    </div>
  );
}

export function NewAppPage() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);
  const [showErrors, setShowErrors] = useState(false);
  // Add-variable row inputs live here so navigating between steps does not lose them.
  const [envDraft, setEnvDraft] = useState<EnvDraft>({ key: '', value: '' });
  const [deploying, setDeploying] = useState(false);
  // Captured once so the generated-name preview is stable across renders.
  const [nowMs] = useState(() => Date.now());
  const deployingRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const stepRef = useRef<HTMLDivElement>(null);

  const { catalogs, endpoints, stacks, domains, loading, stacksLoading, error, stacksError, hasData, retrying, retry, retryStacks } =
    useDeployData(state.catalogId);

  const selectedCatalog = useMemo(
    () => catalogs.find((c) => c.id === state.catalogId) ?? null,
    [catalogs, state.catalogId],
  );
  const selectedStack = useMemo(
    () => stacks.find((s) => s.name === state.stackName) ?? null,
    [stacks, state.stackName],
  );

  const mode = useMemo(() => effectiveTargetMode(selectedStack, selectedCatalog), [selectedStack, selectedCatalog]);
  const locked = useMemo(() => lockedEndpointIds(selectedStack, selectedCatalog), [selectedStack, selectedCatalog]);
  const { fits, recommended } = useMemo(
    () => rankEndpoints(endpoints, selectedStack, mode, locked),
    [endpoints, selectedStack, mode, locked],
  );
  const selectedFit = useMemo(
    () => fits.find((f) => f.endpoint.id === state.endpointId) ?? null,
    [fits, state.endpointId],
  );
  const effectiveEndpoint =
    selectedFit?.endpoint ?? (state.endpointId === null ? (recommended?.endpoint ?? null) : null);
  const blocked = traefikBlocked(selectedCatalog, effectiveEndpoint);
  const envDraftPending = envDraft.key.trim() !== '' || envDraft.value.trim() !== '';
  // A subdomain only applies when the app gets a hostname; otherwise a stale value is ignored.
  const subdomainAvailable = domains.length > 0 && (selectedCatalog?.expose_port ?? 0) > 0;
  const subdomain = subdomainAvailable ? state.subdomain.trim() : '';
  const { valid, reasons, firstInvalid, endpointGone } = computeStepValidity({
    catalogId: selectedCatalog?.id ?? null,
    stackName: selectedStack?.name ?? '',
    endpointId: state.endpointId,
    mode,
    selectedFit,
    hasRecommended: recommended !== null,
    traefikBlocked: blocked,
    externalUserId: state.externalUserId,
    displayName: state.displayName,
    appName: state.appName,
    useCustomName: state.useCustomName,
    instanceName: state.instanceName,
    envVars: state.envVars,
    envDraftPending,
    subdomain,
  });
  const canAdvance = valid[state.step];

  const instanceLabel = state.useCustomName
    ? state.instanceName || 'Custom name (not set)'
    : state.appName || 'Auto-generated on deploy';
  const endpointLabel =
    state.endpointId !== null ? (selectedFit?.endpoint.name ?? 'Unavailable endpoint') : 'Auto-select';
  const endpointNote =
    state.endpointId !== null
      ? null
      : recommended
        ? `Best fit right now: ${recommended.endpoint.name}`
        : 'No endpoint currently fits';

  // A choice that is no longer offered (domain deleted, list down to one) falls back to the default.
  const domain = effectiveDomainChoice(state.domain, domains);
  const domainLabel = domainChoiceLabel(domain, domains);
  const hostLabel = customHostLabel(subdomain, domain, domains);

  const reason = reasons[state.step];

  // Focus moves from event handlers via rAF (never effects): the new step's heading mounts after the dispatch commits.
  const focusHeading = () => {
    requestAnimationFrame(() => headingRef.current?.focus());
  };
  // Focus the first invalid field if there is one; otherwise leave focus where it is (the Next/Deploy button).
  const focusFirstInvalid = (): boolean => {
    const bad = stepRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (bad) requestAnimationFrame(() => bad.focus());
    return bad != null;
  };

  const onSelectCatalog = (catalogId: number) => {
    dispatch({ type: 'selectCatalog', catalogId });
  };

  const onSelectStack = (stackName: string) => {
    const stack = stacks.find((s) => s.name === stackName);
    if (!stack || !selectedCatalog) return;
    const m = effectiveTargetMode(stack, selectedCatalog);
    const l = lockedEndpointIds(stack, selectedCatalog);
    // selectStack is dispatched from user events only (never refetch effects): changed defaults intentionally reset env edits.
    dispatch({
      type: 'selectStack',
      stackName,
      envDefaults: stack.env_vars ?? null,
      allowedEndpointIds: m === 'locked' ? l : null,
    });
    const pre = preselectedEndpointId(rankEndpoints(endpoints, stack, m, l).fits, m);
    // Never overwrite a still-valid selection; preselect only when there is none or the reducer dropped it.
    const dropped = state.endpointId === null || (m === 'locked' && !l.has(state.endpointId));
    if (pre !== null && dropped) dispatch({ type: 'selectEndpoint', endpointId: pre });
  };

  const goTo = (step: WizardStep) => {
    if (deploying) return;
    dispatch({ type: 'goTo', step });
    setShowErrors(false);
    focusHeading();
  };

  const onNext = () => {
    if (!canAdvance) {
      setShowErrors(true);
      focusFirstInvalid();
      return;
    }
    dispatch({ type: 'next', canAdvance: true });
    setShowErrors(false);
    focusHeading();
  };

  const onBack = () => {
    dispatch({ type: 'back' });
    setShowErrors(false);
    focusHeading();
  };

  const onDeploy = async () => {
    if (deployingRef.current) return;
    if (!valid.review || !selectedCatalog || !selectedStack) {
      setShowErrors(true);
      if (firstInvalid && firstInvalid !== state.step) {
        dispatch({ type: 'goTo', step: firstInvalid });
        focusHeading();
      } else {
        focusFirstInvalid();
      }
      return;
    }
    // deployingRef is deliberately left set after success: the page unmounts on navigate, and it blocks double submits meanwhile.
    deployingRef.current = true;
    setDeploying(true);
    try {
      const result = await api.apps.deploy({
        app_name: selectedCatalog.name,
        stack_name: selectedStack.name,
        external_user_id: state.externalUserId,
        display_name: state.displayName.trim() || undefined,
        name: state.appName || (state.useCustomName ? state.instanceName : undefined),
        endpoint_id: state.endpointId ?? undefined,
        env_vars: state.envVars,
        domain: domain || undefined,
        subdomain: subdomain || undefined,
      });
      toast.success('App deployed');
      const notice = subdomainFallbackNotice(subdomain, result.full_subdomain);
      if (notice) toast.info(notice);
      navigate(`/apps/${result.app_id}?tab=deployments`);
    } catch (err) {
      toast.error(mapDeployError(err instanceof Error ? err.message : '', state.endpointId !== null, errorDetailCode(err)));
      deployingRef.current = false;
      setDeploying(false);
    }
  };

  const header = (
    <PageHeader title="Deploy new app" description="Choose what to run and where, then review and deploy." />
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {header}
        <LoadingSkeleton />
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="space-y-6">
        {header}
        <EmptyState
          icon={TriangleAlert}
          title="Could not load deploy data"
          description={error ?? 'Failed to load'}
          action={
            <Button
              variant="outline"
              size="sm"
              aria-disabled={retrying}
              onClick={() => {
                if (!retrying) retry();
              }}
            >
              {retrying && <Spinner />}
              {retrying ? 'Retrying…' : 'Retry'}
            </Button>
          }
        />
      </div>
    );
  }

  if (catalogs.length === 0) {
    return (
      <div className="space-y-6">
        {header}
        <EmptyState
          icon={PackageOpen}
          title="No catalogs available"
          description="Create an active catalog before deploying an app."
          action={
            <Link to="/catalog" className={buttonVariants({ size: 'sm' })}>
              Go to catalog
            </Link>
          }
        />
      </div>
    );
  }

  const isReview = state.step === 'review';

  return (
    <div className="space-y-6">
      {header}

      {error !== null && (
        <div
          role="alert"
          className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
        >
          <span>Showing last known data — refresh failed.</span>
          <Button
            variant="ghost"
            size="xs"
            aria-disabled={retrying}
            onClick={() => {
              if (!retrying) retry();
            }}
          >
            {retrying ? 'Retrying…' : 'Retry'}
          </Button>
        </div>
      )}

      <WizardStepper steps={STEP_LIST} current={state.step} maxStep={state.maxStep} onGoTo={goTo} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-6">
          <div ref={stepRef} className="space-y-4">
            {state.step === 'source' && (
              <SourceStep
                catalogs={catalogs}
                stacks={stacks}
                stacksLoading={stacksLoading}
                stacksError={stacksError}
                onRetryStacks={retryStacks}
                catalogId={state.catalogId}
                stackName={state.stackName}
                onSelectCatalog={onSelectCatalog}
                onSelectStack={onSelectStack}
                headingRef={headingRef}
              />
            )}

            {state.step === 'target' && (
              <>
                {endpointGone && (
                  <div
                    role="alert"
                    className="rounded-md border border-destructive/50 p-3 text-sm font-medium text-destructive"
                  >
                    The selected endpoint is no longer available. Choose another.
                  </div>
                )}
                <TargetStep
                  fits={fits}
                  recommended={recommended}
                  mode={mode}
                  endpointId={state.endpointId}
                  onSelect={(endpointId) => dispatch({ type: 'selectEndpoint', endpointId })}
                  catalog={selectedCatalog}
                  effectiveEndpoint={effectiveEndpoint}
                  loading={false}
                  hasLockedEndpoints={locked.size > 0}
                  headingRef={headingRef}
                  domains={domains}
                  domain={domain}
                  onSelectDomain={(domain) => dispatch({ type: 'selectDomain', domain })}
                />
              </>
            )}

            {state.step === 'configure' && selectedCatalog && (
              <ConfigureStep
                values={{
                  appName: state.appName,
                  externalUserId: state.externalUserId,
                  displayName: state.displayName,
                  useCustomName: state.useCustomName,
                  instanceName: state.instanceName,
                  subdomain: state.subdomain,
                }}
                onChangeField={(field: ConfigureField, value: string) => dispatch({ type: 'setField', field, value })}
                onToggleCustomName={(value) => dispatch({ type: 'setUseCustomName', value })}
                onChangeSubdomain={(value) => dispatch({ type: 'setSubdomain', value })}
                subdomainAvailable={subdomainAvailable}
                showErrors={showErrors}
                catalogName={selectedCatalog.name}
                nowMs={nowMs}
                envVars={state.envVars}
                envDefaults={state.envDefaults}
                onSetEnv={(key, value) => dispatch({ type: 'setEnv', key, value })}
                onAddEnv={(key, value) => dispatch({ type: 'addEnv', key, value })}
                onRemoveEnv={(key) => dispatch({ type: 'removeEnv', key })}
                envDraft={envDraft}
                onEnvDraftChange={setEnvDraft}
                headingRef={headingRef}
              />
            )}

            {isReview && selectedCatalog && selectedStack && (
              <>
                <ReviewStep
                  catalog={selectedCatalog}
                  stack={selectedStack}
                  endpointLabel={endpointLabel}
                  endpointNote={endpointNote}
                  domainLabel={domainLabel}
                  hostLabel={hostLabel}
                  externalUserId={state.externalUserId}
                  displayName={state.displayName}
                  appName={state.appName}
                  instanceLabel={instanceLabel}
                  envVars={state.envVars}
                  onEdit={(step: ReviewTarget) => goTo(step)} // goTo ignores calls while deploying
                  headingRef={headingRef}
                />
                <ComposePreview yaml={selectedStack.compose_content} />
              </>
            )}
          </div>

          <footer className="flex flex-wrap items-center gap-3 border-t pt-4">
            {state.step !== 'source' && (
              <Button type="button" variant="outline" onClick={onBack} disabled={deploying}>
                Back
              </Button>
            )}
            <p
              id="wizard-reason"
              role="status"
              aria-live="polite"
              className={`min-w-0 shrink grow basis-full order-first text-sm sm:order-none sm:basis-0 ${showErrors && !canAdvance ? 'font-medium text-destructive' : 'text-muted-foreground'}`}
            >
              {/* Always mounted; the "Can't continue" prefix changes the text so a failed attempt is announced. */}
              {canAdvance ? '' : `${showErrors ? "Can't continue: " : ''}${reason ?? ''}`}
            </p>
            <Link to="/apps" className={buttonVariants({ variant: 'ghost' })}>
              Cancel
            </Link>
            {isReview ? (
              <Button
                type="button"
                aria-disabled={!canAdvance || deploying}
                aria-describedby="wizard-reason"
                onClick={onDeploy}
              >
                {deploying && <Spinner />}
                {deploying ? 'Deploying…' : 'Deploy'}
              </Button>
            ) : (
              <Button
                type="button"
                // aria-disabled (not disabled) keeps Next focusable so a failed press can announce why.
                aria-disabled={!canAdvance}
                aria-describedby="wizard-reason"
                onClick={onNext}
              >
                Next
              </Button>
            )}
          </footer>
        </div>

        <DeploySummary
          catalogName={selectedCatalog?.name ?? null}
          stackName={selectedStack?.name ?? null}
          stack={selectedStack}
          endpointLabel={selectedStack ? endpointLabel : null}
          endpointNote={selectedStack ? endpointNote : null}
          domainLabel={selectedStack ? domainLabel : null}
          hostLabel={selectedStack ? hostLabel : null}
          externalUserId={state.externalUserId || null}
          instanceLabel={selectedStack ? instanceLabel : null}
        />
      </div>
    </div>
  );
}

